/**
 * <holster-fit-finder> — guided "Find your fit" tool for the Mainstay IWB Holster.
 *
 * Flow: the customer searches their handgun (typeahead over the curated dataset
 * server-rendered into <script data-hff-data>), then answers Optic (yes/no) and
 * Light (yes/no). The tool resolves the correct holster Size code and drives
 * Horizon's native <variant-picker> — checking the matching "Size" radio (and the
 * "Magazine Pouch" radio from the result-card toggle) so price / availability /
 * the add-to-cart variant all update natively. "Add to cart" clicks Horizon's real
 * add button, so the cart drawer opens; the finder then resets for the next gun.
 *
 * Progressive enhancement: the native variant picker + buy buttons are only hidden
 * (via the `hff-on` class this script adds to <html>) once we've confirmed we can
 * drive them. If this script fails to load, the native controls remain usable.
 *
 * Dataset entry shape: { gun, base, optic, light, optic_light } — each size field
 * is a size code (e.g. "5C", "7-RDO") or null when that setup isn't offered.
 */
class HolsterFitFinder extends HTMLElement {
  connectedCallback() {
    this.data = this.#loadData();
    if (!this.data) return;

    this.gunInput = this.querySelector('[data-hff-gun-input]');
    this.menu = this.querySelector('[data-hff-menu]');
    this.selected = this.querySelector('[data-hff-selected]');
    this.selName = this.querySelector('[data-hff-sel-name]');
    this.selMeta = this.querySelector('[data-hff-sel-meta]');
    this.changeBtn = this.querySelector('[data-hff-change]');
    this.chips = this.querySelector('[data-hff-chips]');
    this.chipsLabel = this.querySelector('[data-hff-chips-label]');
    this.opticSel = this.querySelector('[data-hff-optic]');
    this.lightSel = this.querySelector('[data-hff-light]');
    this.opticNote = this.querySelector('[data-hff-optic-note]');
    this.lightNote = this.querySelector('[data-hff-light-note]');
    this.result = this.querySelector('[data-hff-result]');
    this.rSize = this.querySelector('[data-hff-size]');
    this.rConfig = this.querySelector('[data-hff-config]');
    this.rLabel = this.querySelector('[data-hff-result-label]');
    this.pouch = this.querySelector('[data-hff-pouch]');
    this.atc = this.querySelector('[data-hff-atc]');
    this.manualToggle = this.querySelector('[data-hff-manual-toggle]');
    this.manualWrap = this.querySelector('[data-hff-manual-wrap]');
    this.manualSel = this.querySelector('[data-hff-manual]');
    if (!this.gunInput || !this.opticSel) return;

    this.f2 = this.opticSel.closest('[data-hff-field]');
    this.f3 = this.lightSel.closest('[data-hff-field]');
    this.guns = this.data.guns.slice().sort((a, b) => this.#groupSort(a, b));
    this.variants = this.#loadJSON('[data-hff-variants]') || []; // [{o:[opt1,opt2], id}]
    this.selectedGun = null;
    this.hasOptic = false;
    this.hasLight = false;
    this.currentSize = null;
    this.activeIdx = -1;

    this.#populateManual();
    this.#buildChips();

    // Take over the native UI only if we can actually drive it.
    if (this.#sizeFieldset() && this.#nativeAddBtn()) {
      document.documentElement.classList.add('hff-on');
      this.#lock(true); // gate Shop Pay until a size is resolved
    }

    this.gunInput.addEventListener('focus', () => this.#renderMenu(this.gunInput.value));
    this.gunInput.addEventListener('input', () => { this.activeIdx = -1; this.#renderMenu(this.gunInput.value); });
    this.gunInput.addEventListener('keydown', (e) => this.#onKey(e));
    document.addEventListener('click', (e) => { if (!e.target.closest('.hff__combo')) this.#closeMenu(); });
    this.changeBtn.addEventListener('click', () => this.#changeGun());
    this.opticSel.addEventListener('change', () => this.#update());
    this.lightSel.addEventListener('change', () => this.#update());
    this.pouch.addEventListener('change', () => { this.#applyPouch(); this.#syncAvailability(); });
    this.atc.addEventListener('click', () => this.#addToCart());
    this.manualToggle.addEventListener('click', () => this.#toggleManual());
    this.manualSel.addEventListener('change', () => { if (this.manualSel.value) this.#showManual(this.manualSel.value); });
  }

  /* ---------- data ---------- */

  #loadData() {
    try { return JSON.parse(this.querySelector('script[data-hff-data]').textContent); }
    catch { return null; }
  }
  #loadJSON(sel) {
    try { return JSON.parse(this.querySelector(sel).textContent); }
    catch { return null; }
  }
  /** Exact variant id for a size (+ magazine-pouch value). Falls back to size-only
   *  for products without a pouch option (e.g. the combo bundle). */
  #variantId(size, pouchValue) {
    let v = this.variants.find((x) => x.o[0] === size && x.o[1] === pouchValue);
    if (!v) v = this.variants.find((x) => x.o[0] === size);
    return v ? v.id : null;
  }

  #BRANDS = ['Sig Sauer', 'Kel-Tec', 'Rost Martin', 'Para Ordnance', 'Heizer Defense', 'Star Firestar', 'S&W', 'H&K', 'Springfield', 'Browning', 'Diamondback', 'Kimber', 'Walther', 'Beretta', 'Canik', 'Bersa', 'Colt', 'Glock', 'Ruger', 'Taurus', 'Kahr', 'FN', 'CZ', 'SCCY', 'Staccato', 'Mauser', 'AMT', 'Sig', 'HK'];
  #makeOf(name) {
    for (const b of this.#BRANDS) {
      if (name === b || name.startsWith(b + ' ') || name.startsWith(b + '-')) return b === 'S&W' ? 'Smith & Wesson' : b;
    }
    return 'Revolvers, 1911 & other';
  }
  #groupSort(a, b) {
    const ma = this.#makeOf(a.gun), mb = this.#makeOf(b.gun);
    const oa = ma === 'Revolvers, 1911 & other' ? 1 : 0, ob = mb === 'Revolvers, 1911 & other' ? 1 : 0;
    if (oa !== ob) return oa - ob;
    if (ma !== mb) return ma.localeCompare(mb);
    return a.gun.localeCompare(b.gun);
  }

  #POPULAR = { 'Glock 19': 'Glock 19', 'Sig Sauer P365': 'Sig P365', 'S&W M&P Shield Plus': 'M&P Shield Plus', 'Springfield Hellcat': 'Hellcat', 'Glock 43x': 'Glock 43X' };
  #buildChips() {
    const frag = document.createDocumentFragment();
    for (const [name, label] of Object.entries(this.#POPULAR)) {
      const g = this.guns.find((x) => x.gun === name);
      if (!g) continue;
      const c = document.createElement('button');
      c.type = 'button'; c.className = 'hff__chip'; c.textContent = label;
      c.addEventListener('click', () => this.#selectGun(g));
      frag.appendChild(c);
    }
    this.chips.appendChild(frag);
  }
  #populateManual() {
    const frag = document.createDocumentFragment();
    for (const s of this.data.sizes) {
      const o = document.createElement('option');
      o.value = s; o.textContent = 'Size ' + s;
      frag.appendChild(o);
    }
    this.manualSel.appendChild(frag);
  }

  /* ---------- native variant picker drivers ---------- */

  #fieldsetByLegend(nameLc) {
    return [...document.querySelectorAll('variant-picker fieldset, fieldset')].find(
      (f) => (f.querySelector('legend')?.textContent || '').trim().toLowerCase().startsWith(nameLc)
    );
  }
  #sizeFieldset() { return this.#fieldsetByLegend('size'); }
  #pouchFieldset() { return this.#fieldsetByLegend('magazine pouch'); }
  #nativeAddBtn() { return document.querySelector('product-form-component button[name="add"]'); }

  /** Gate the native Shop Pay / express checkout until a size is resolved. */
  #lock(locked) { document.documentElement.classList.toggle('hff-needs-gun', locked); }

  #setOption(fs, value) {
    if (!fs) return false;
    const radio = fs.querySelector(`input[type="radio"][value="${CSS.escape(value)}"]`);
    if (!radio) return false;
    if (!radio.checked) {
      radio.checked = true;
      radio.dispatchEvent(new Event('input', { bubbles: true }));
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }
    return true;
  }
  #selectSize(size) { return this.#setOption(this.#sizeFieldset(), size); }
  #applyPouch() { this.#setOption(this.#pouchFieldset(), this.pouch.checked ? 'Add Magazine Pouch' : 'No Magazine Pouch'); }

  /** Reflect the selected variant's stock in the CTA (Horizon disables the native
   *  add button for a sold-out variant). */
  #syncAvailability() {
    const btn = this.#nativeAddBtn();
    requestAnimationFrame(() => {
      const soldout = !!(btn && btn.disabled);
      this.atc.disabled = soldout;
      this.atc.textContent = soldout ? 'Sold out' : 'Add to cart';
    });
  }

  /* ---------- combobox ---------- */

  #esc(s) { return String(s).replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c])); }

  #renderMenu(q) {
    const query = q.trim().toLowerCase();
    const list = query ? this.guns.filter((g) => g.gun.toLowerCase().includes(query)) : this.guns;
    this.menu.innerHTML = '';
    if (!list.length) {
      this.menu.innerHTML = '<div class="hff__opt hff__opt--empty">No match — use “Choose your size manually” below.</div>';
      this.#openMenu();
      return;
    }
    let lastMake = '';
    const frag = document.createDocumentFragment();
    for (const g of list) {
      const mk = this.#makeOf(g.gun);
      if (mk !== lastMake) {
        const grp = document.createElement('div');
        grp.className = 'hff__menu-group'; grp.textContent = mk;
        frag.appendChild(grp); lastMake = mk;
      }
      const o = document.createElement('div');
      o.className = 'hff__opt'; o.setAttribute('role', 'option');
      o.innerHTML = '<span>' + this.#esc(g.gun) + '</span><span class="hff__size-tag">Size ' + this.#esc(g.base) + '</span>';
      o.addEventListener('click', () => this.#selectGun(g));
      frag.appendChild(o);
    }
    this.menu.appendChild(frag);
    this.#openMenu();
  }
  #openMenu() { this.menu.classList.add('is-open'); this.gunInput.setAttribute('aria-expanded', 'true'); }
  #closeMenu() { this.menu.classList.remove('is-open'); this.gunInput.setAttribute('aria-expanded', 'false'); }

  #onKey(e) {
    const opts = [...this.menu.querySelectorAll('.hff__opt:not(.hff__opt--empty)')];
    if (e.key === 'ArrowDown') { e.preventDefault(); this.activeIdx = Math.min(this.activeIdx + 1, opts.length - 1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); this.activeIdx = Math.max(this.activeIdx - 1, 0); }
    else if (e.key === 'Enter' && this.activeIdx >= 0) { e.preventDefault(); opts[this.activeIdx].click(); return; }
    else if (e.key === 'Escape') { this.#closeMenu(); return; }
    else return;
    opts.forEach((o, i) => o.classList.toggle('is-active', i === this.activeIdx));
    if (opts[this.activeIdx]) opts[this.activeIdx].scrollIntoView({ block: 'nearest' });
  }

  /* ---------- selection flow ---------- */

  #gateStep(sel, note, field, enabled, msg) {
    sel.value = 'no'; sel.disabled = !enabled;
    field.classList.remove('hff__field--locked');
    field.classList.toggle('hff__field--na', !enabled);
    if (enabled) { note.hidden = true; note.textContent = ''; }
    else { note.hidden = false; note.textContent = msg; }
  }

  #selectGun(g) {
    this.selectedGun = g;
    this.hasOptic = !!(g.optic || g.optic_light);
    this.hasLight = !!(g.light || g.optic_light);
    this.#closeMenu();
    this.gunInput.hidden = true;
    this.chipsLabel.hidden = true;
    this.chips.hidden = true;
    this.selName.textContent = g.gun;
    this.selMeta.textContent = 'Starts at size ' + g.base;
    this.selected.classList.add('is-shown');
    this.#gateStep(this.opticSel, this.opticNote, this.f2, this.hasOptic, 'This model isn’t offered with a red-dot cut — sizing is the same either way.');
    this.#gateStep(this.lightSel, this.lightNote, this.f3, this.hasLight, 'This model isn’t offered with a light/laser cut.');
    this.#closeManual();
    this.#update();
  }

  #changeGun() {
    this.selectedGun = null; this.hasOptic = this.hasLight = false;
    this.selected.classList.remove('is-shown');
    this.gunInput.hidden = false; this.gunInput.value = '';
    this.chipsLabel.hidden = false; this.chips.hidden = false;
    for (const [sel, fld] of [[this.opticSel, this.f2], [this.lightSel, this.f3]]) {
      sel.value = 'no'; sel.disabled = true;
      fld.classList.add('hff__field--locked');
      fld.classList.remove('hff__field--done', 'hff__field--na');
    }
    this.opticNote.hidden = true; this.lightNote.hidden = true;
    this.pouch.checked = false;
    this.currentSize = null;
    this.#lock(true);
    this.result.hidden = true; this.result.classList.remove('hff__result--warn');
  }

  #update() {
    if (!this.selectedGun) { this.result.hidden = true; return; }
    const optic = this.hasOptic && this.opticSel.value === 'yes';
    const light = this.hasLight && this.lightSel.value === 'yes';
    this.f2.classList.add('hff__field--done');
    this.f3.classList.add('hff__field--done');
    let size = this.selectedGun.base;
    if (optic && light) size = this.selectedGun.optic_light;
    else if (optic) size = this.selectedGun.optic;
    else if (light) size = this.selectedGun.light;
    const parts = [optic ? 'red dot optic' : 'no optic', light ? 'light or laser' : 'no light'];
    if (!size) return this.#showWarn(this.selectedGun.gun, parts);
    this.#showFit(size, this.selectedGun.gun, parts);
  }

  #showFit(size, gunName, parts) {
    this.result.classList.remove('hff__result--warn');
    this.result.hidden = false;
    this.rLabel.textContent = 'Your fit';
    this.rSize.textContent = 'Size ' + size;
    this.rConfig.textContent = 'Fits your ' + gunName + ' · ' + parts.join(' · ') + '.';
    this.currentSize = size;
    this.pouch.checked = false;
    this.#selectSize(size);
    this.#applyPouch();
    this.#lock(false);
    this.#syncAvailability();
  }

  #showWarn(gunName, parts) {
    this.result.classList.add('hff__result--warn');
    this.result.hidden = false;
    this.rLabel.textContent = 'Not offered in that setup';
    this.rSize.textContent = 'No matching size';
    this.rConfig.textContent = 'We don’t list a Mainstay for the ' + gunName + ' with ' + parts.join(' + ') +
      '. Remove the optic or light above, or choose your size manually below.';
    this.currentSize = null;
    this.#lock(true);
  }

  #showManual(size) {
    this.#changeGun();
    this.result.classList.remove('hff__result--warn');
    this.result.hidden = false;
    this.rLabel.textContent = 'Your fit';
    this.rSize.textContent = 'Size ' + size;
    this.rConfig.textContent = 'Manually selected. Double-check it against the size chart in the product description below.';
    this.currentSize = size;
    this.pouch.checked = false;
    this.#selectSize(size);
    this.#applyPouch();
    this.#lock(false);
    this.#syncAvailability();
  }

  #toggleManual() {
    const open = this.manualWrap.hidden;
    this.manualWrap.hidden = !open;
    this.manualToggle.setAttribute('aria-expanded', String(open));
    if (open) this.manualSel.focus();
  }
  #closeManual() { this.manualWrap.hidden = true; this.manualToggle.setAttribute('aria-expanded', 'false'); this.manualSel.value = ''; }

  /* ---------- add to cart ---------- */

  #addToCart() {
    const btn = this.#nativeAddBtn();
    if (!btn || btn.disabled || !this.currentSize) { this.#syncAvailability(); return; }
    this.#applyPouch();
    // Set the variant id explicitly so the add can't race the variant-picker's
    // async update of the hidden input. Horizon's submit reads input[name="id"].
    const pouchValue = this.pouch.checked ? 'Add Magazine Pouch' : 'No Magazine Pouch';
    const id = this.#variantId(this.currentSize, pouchValue);
    const idInput = document.querySelector('product-form-component input[name="id"]');
    if (id && idInput) idInput.value = id;
    btn.click(); // Horizon adds to cart and opens the drawer
    this.#changeGun(); // reset for the next handgun
    this.gunInput.focus();
  }
}

if (!customElements.get('holster-fit-finder')) {
  customElements.define('holster-fit-finder', HolsterFitFinder);
}
